import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  ArrowLeft,
  Bell,
  Check,
  Handshake,
  Info,
  Loader2,
  Mail,
  MessageSquareText,
  Monitor,
  Play,
  UserPlus,
  Volume2,
  VolumeX,
} from "lucide-react";
import { toast } from "sonner";
import type {
  ChannelPrefs,
  NotificationPrefs,
  NotificationSound,
  NotificationType,
} from "@bookdental/schemas";
import {
  DEFAULT_NOTIFICATION_PREFS,
  notificationPrefsSchema,
} from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { playNotificationSound, SOUND_OPTIONS } from "@/lib/notification-sound";
import { cn } from "@/lib/cn";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { useOcupaRodape } from "@/components/ajuda/RodapeOcupado";
import { CONFIG_NOTIFICACOES_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";

type SectionKey = "sound" | "types";

type TypeMeta = {
  type: NotificationType;
  label: string;
  description: string;
  accent: string;
  icon: React.ComponentType<{ className?: string }>;
};

const TYPES: TypeMeta[] = [
  {
    type: "PATIENT_INVITE_ACCEPTED",
    label: "Paciente cadastrado via link",
    description: "Alguém preencheu o link de cadastro que você gerou.",
    accent: "#2457ff",
    icon: UserPlus,
  },
  {
    type: "PARTNER_INVITE_ACCEPTED",
    label: "Parceiro aceitou convite",
    description: "Dentista, lab ou fornecedor aceitou a parceria.",
    accent: "#0ea5e9",
    icon: Handshake,
  },
];

const SECTIONS: {
  key: SectionKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
}[] = [
  {
    key: "sound",
    label: "Som",
    description: "Ligar ou desligar sons globalmente",
    icon: <Volume2 className="h-4 w-4" />,
    accent: "#10b981",
  },
  {
    key: "types",
    label: "Tipos de aviso",
    description: "Canal e som por tipo de evento",
    icon: <Bell className="h-4 w-4" />,
    accent: "#2457ff",
  },
];

/** `embutido`: dentro de uma seção de outra página (Configurações, Minha conta) — sem título
 *  próprio, sem "voltar" e com as subseções em abas, já que a de fora tem a barra lateral. */
export function SettingsNotifications({ embutido = false }: { embutido?: boolean } = {}) {
  const ocupaRodape = useOcupaRodape<HTMLDivElement>();
  const compacto = useIsMobile() || embutido;
  const isMobile = useIsMobile();
  // Embutida, quem registra o tour é a página de fora (registrar aqui seria sobrescrito).
  useTourDaPagina(embutido ? [] : CONFIG_NOTIFICACOES_TOUR_STEPS);
  useSetPageHeader(
    {
      title: isMobile
        ? "Notificações"
        : "Configurações · Notificações",
      subtitle: isMobile ? undefined : "Controle onde e como você recebe avisos",
    },
    [isMobile],
    !embutido,
  );

  const [section, setSection] = useState<SectionKey>("sound");
  const utils = trpc.useUtils();
  const prefsQuery = trpc.notification.getPrefs.useQuery();
  const [local, setLocal] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (prefsQuery.data) {
      const parsed = notificationPrefsSchema.safeParse(prefsQuery.data);
      setLocal(parsed.success ? parsed.data : DEFAULT_NOTIFICATION_PREFS);
      setDirty(false);
    }
  }, [prefsQuery.data]);

  const save = trpc.notification.setPrefs.useMutation({
    onSuccess: () => {
      utils.notification.getPrefs.invalidate();
      setDirty(false);
      toast.success("Preferências salvas.");
    },
    onError: (err) => toast.error(err.message || "Falha ao salvar."),
  });

  const updateType = (
    type: NotificationType,
    patch: Partial<{ channels: ChannelPrefs; sound: NotificationSound }>,
  ) => {
    setLocal((prev) => {
      const current =
        prev.perType[type] ?? DEFAULT_NOTIFICATION_PREFS.perType[type]!;
      return {
        ...prev,
        perType: {
          ...prev.perType,
          [type]: { ...current, ...patch },
        },
      };
    });
    setDirty(true);
  };

  const updateSoundEnabled = (v: boolean) => {
    setLocal((prev) => ({ ...prev, soundEnabled: v }));
    setDirty(true);
  };

  const enabledCount = useMemo(() => {
    return TYPES.reduce((acc, { type }) => {
      const c = local.perType[type]?.channels;
      if (!c) return acc;
      return (
        acc + (c.platform ? 1 : 0) + (c.email ? 1 : 0) + (c.whatsapp ? 1 : 0)
      );
    }, 0);
  }, [local]);

  if (prefsQuery.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-6xl items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-primary" />
      </div>
    );
  }

  const activeSection = SECTIONS.find((s) => s.key === section)!;

  return (
    <div className={`${embutido ? "" : "mx-auto max-w-6xl "}w-full space-y-4 pb-24`}>
      {!embutido && (
        <Link
          to="/configuracoes"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Configurações
        </Link>
      )}

      {compacto ? <MobileTabs section={section} onChange={setSection} /> : null}

      <div className="flex gap-6">
        {!compacto && <DesktopSidebar section={section} onChange={setSection} />}

        <div className="min-w-0 flex-1">
          <div className="rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
            <div className="flex items-center gap-3 border-b border-zinc-100 px-6 py-4 dark:border-border">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: `${activeSection.accent}1a`,
                  color: activeSection.accent,
                }}
              >
                {activeSection.icon}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
                  {activeSection.label}
                </h2>
                <p className="text-xs text-zinc-500 dark:text-muted-foreground">
                  {activeSection.description}
                </p>
              </div>
              {section === "types" && (
                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                  {enabledCount} canais ativos
                </span>
              )}
            </div>

            <div className="px-4 py-5 sm:px-6">
              {section === "sound" && (
                <SoundSection
                  enabled={local.soundEnabled}
                  onChange={updateSoundEnabled}
                />
              )}
              {section === "types" && (
                <TypesSection
                  prefs={local}
                  onChange={updateType}
                  soundEnabled={local.soundEnabled}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer sticky — só aparece quando há mudanças */}
      {dirty && (
        <div
          ref={ocupaRodape}
          // No celular fica em cima da barra de abas, que já cobre a área do gesto.
          className="fixed bottom-0 left-0 right-0 z-30 border-t border-zinc-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm max-md:bottom-[calc(3.75rem+env(safe-area-inset-bottom))] max-md:pb-0 dark:border-border dark:bg-card/95"
        >
          <div className="mx-auto flex w-full max-w-6xl items-center justify-end gap-2 px-4 py-3 sm:px-6 md:px-8">
            <button
              type="button"
              onClick={() => {
                if (prefsQuery.data) {
                  const parsed = notificationPrefsSchema.safeParse(
                    prefsQuery.data,
                  );
                  setLocal(
                    parsed.success ? parsed.data : DEFAULT_NOTIFICATION_PREFS,
                  );
                }
                setDirty(false);
              }}
              disabled={save.isPending}
              className="inline-flex h-10 items-center rounded-md border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-60 sm:h-9 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={() => save.mutate(local)}
              disabled={save.isPending}
              className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:px-4 sm:font-medium"
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {save.isPending ? "Salvando..." : "Salvar preferências"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Seção Som ────────────────────────────────────────────

function SoundSection({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      <div
        data-tour="config-notificacoes-som"
        className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50/40 p-4 dark:border-border dark:bg-muted/20"
      >
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-lg",
              enabled
                ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
                : "bg-zinc-100 text-zinc-400 dark:bg-muted dark:text-muted-foreground",
            )}
          >
            {enabled ? (
              <Volume2 className="h-5 w-5" />
            ) : (
              <VolumeX className="h-5 w-5" />
            )}
          </span>
          <div>
            <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">
              {enabled ? "Sons ligados" : "Sons desligados"}
            </p>
            <p className="text-[12px] text-zinc-500 dark:text-muted-foreground">
              {enabled
                ? "Você vai ouvir o som quando chegarem notificações."
                : "Avisos silenciosos — apenas visual no sino."}
            </p>
          </div>
        </div>
        <Toggle checked={enabled} onChange={onChange} label="Ativar som" />
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-dashed border-zinc-200 bg-white p-3 text-[12px] leading-relaxed text-zinc-600 dark:border-border dark:bg-card dark:text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>
          O som toca somente quando a aba do DentalBook está aberta. Cada tipo
          de aviso usa o som configurado em <strong>Tipos de aviso</strong>.
        </p>
      </div>
    </div>
  );
}

// ─── Seção Tipos de aviso ─────────────────────────────────

function TypesSection({
  prefs,
  onChange,
  soundEnabled,
}: {
  prefs: NotificationPrefs;
  onChange: (
    type: NotificationType,
    patch: Partial<{ channels: ChannelPrefs; sound: NotificationSound }>,
  ) => void;
  soundEnabled: boolean;
}) {
  return (
    <ul className="divide-y divide-zinc-100 dark:divide-border">
      {TYPES.map((meta) => {
        const pref =
          prefs.perType[meta.type] ??
          DEFAULT_NOTIFICATION_PREFS.perType[meta.type]!;
        return (
          <li key={meta.type} className="py-4 first:pt-0 last:pb-0">
            <TypeRow
              meta={meta}
              channels={pref.channels}
              sound={pref.sound}
              soundGlobalEnabled={soundEnabled}
              onChannelsChange={(c) => onChange(meta.type, { channels: c })}
              onSoundChange={(s) => onChange(meta.type, { sound: s })}
            />
          </li>
        );
      })}
      <li className="pt-4">
        <div className="flex items-start gap-2 rounded-lg border border-dashed border-zinc-200 bg-white p-3 text-[11.5px] leading-relaxed text-zinc-500 dark:border-border dark:bg-card dark:text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            Canais de <strong>e-mail</strong> e <strong>WhatsApp</strong>{" "}
            salvam a sua preferência agora — o envio real entra assim que a
            integração de disparo for ativada. A plataforma (sino) funciona
            hoje.
          </p>
        </div>
      </li>
    </ul>
  );
}

// ─── Linha de tipo ────────────────────────────────────────

function TypeRow({
  meta,
  channels,
  sound,
  soundGlobalEnabled,
  onChannelsChange,
  onSoundChange,
}: {
  meta: TypeMeta;
  channels: ChannelPrefs;
  sound: NotificationSound;
  soundGlobalEnabled: boolean;
  onChannelsChange: (c: ChannelPrefs) => void;
  onSoundChange: (s: NotificationSound) => void;
}) {
  const Icon = meta.icon;
  const soundDisabled = !soundGlobalEnabled || sound === "none";

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${meta.accent}1a`, color: meta.accent }}
        >
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            {meta.label}
          </p>
          <p className="mt-0.5 text-[11.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
            {meta.description}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 pl-12">
        <ChannelChip
          label="Plataforma"
          sublabel="Sino"
          icon={<Monitor className="h-3.5 w-3.5" />}
          active={channels.platform}
          onToggle={() =>
            onChannelsChange({ ...channels, platform: !channels.platform })
          }
        />
        <ChannelChip
          label="E-mail"
          icon={<Mail className="h-3.5 w-3.5" />}
          active={channels.email}
          onToggle={() =>
            onChannelsChange({ ...channels, email: !channels.email })
          }
        />
        <ChannelChip
          label="WhatsApp"
          icon={<MessageSquareText className="h-3.5 w-3.5" />}
          active={channels.whatsapp}
          onToggle={() =>
            onChannelsChange({ ...channels, whatsapp: !channels.whatsapp })
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-12">
        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          Som
        </span>
        <div className="relative">
          <select
            value={sound}
            onChange={(e) => onSoundChange(e.target.value as NotificationSound)}
            className="h-8 appearance-none rounded-md border border-zinc-200 bg-white pl-2.5 pr-7 text-xs font-medium text-zinc-700 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 dark:border-border dark:bg-background dark:text-foreground"
          >
            {SOUND_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400">
            ▾
          </span>
        </div>
        <button
          type="button"
          onClick={() => playNotificationSound(sound)}
          disabled={soundDisabled}
          className="inline-flex h-8 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 text-[11px] font-medium text-zinc-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:opacity-40 disabled:hover:bg-white dark:border-border dark:bg-background dark:text-foreground dark:hover:border-primary/40 dark:hover:bg-primary/10 dark:hover:text-primary"
          title={
            soundDisabled
              ? soundGlobalEnabled
                ? "Selecione um som"
                : "Ative o som global primeiro"
              : "Ouvir som"
          }
        >
          <Play className="h-3 w-3" />
          Testar
        </button>
      </div>
    </div>
  );
}

function ChannelChip({
  label,
  sublabel,
  icon,
  active,
  onToggle,
}: {
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[11.5px] font-semibold transition-all",
        active
          ? "border-brand-500 bg-brand-50 text-brand-900 dark:border-primary dark:bg-primary/10 dark:text-primary"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:text-zinc-900 dark:border-border dark:bg-background dark:text-foreground/70 dark:hover:border-border dark:hover:text-foreground",
      )}
    >
      {icon}
      {label}
      {sublabel && (
        <span className="text-[9.5px] font-normal uppercase opacity-70">
          · {sublabel}
        </span>
      )}
      {active && <Check className="h-3 w-3" />}
    </button>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked
          ? "bg-brand-600 dark:bg-primary"
          : "bg-zinc-200 dark:bg-muted",
      )}
    >
      <span
        className={cn(
          "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-[22px]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

// ─── Sidebar + mobile tabs (mesmo padrão SettingsAgenda) ──

function DesktopSidebar({
  section,
  onChange,
}: {
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <aside data-tour="config-notificacoes-tabs" className="w-60 shrink-0">
      <nav className="sticky top-20 space-y-1">
        {SECTIONS.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? "bg-zinc-50 text-zinc-900 dark:bg-muted/40 dark:text-foreground"
                  : "text-zinc-700 hover:bg-zinc-50 dark:text-foreground/80 dark:hover:bg-muted"
              }`}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-transform group-hover:scale-105"
                style={{
                  backgroundColor: `${s.accent}1a`,
                  color: s.accent,
                }}
              >
                {s.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground group-hover:text-zinc-600">
                  {s.description}
                </p>
              </div>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function MobileTabs({
  section,
  onChange,
}: {
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <div
      data-tour="config-notificacoes-tabs"
      className="-mx-4 overflow-x-auto border-b border-zinc-200 bg-white sm:-mx-6 dark:border-border dark:bg-card"
    >
      <div className="flex gap-1 px-4 sm:px-6">
        {SECTIONS.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                active
                  ? "border-brand-600 text-brand-700 dark:border-primary dark:text-primary"
                  : "border-transparent text-zinc-600 hover:text-zinc-900 dark:text-foreground/70 dark:hover:text-foreground"
              }`}
            >
              {s.icon}
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
